import { supabase } from '@/lib/supabase';
import { RealtimeChannel } from '@supabase/supabase-js';

export interface Notification {
  id: string;
  user_id: string;
  shipment_id?: string;
  event_id?: string;
  type: 'milestone' | 'exception' | 'status_change' | 'delay' | 'delivery';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  title: string;
  message: string;
  channels_sent: Array<'in_app' | 'push' | 'email' | 'sms'>;
  is_read: boolean;
  read_at?: string;
  expires_at?: string;
  metadata?: {
    location?: string;
    latitude?: number;
    longitude?: number;
    tracking_number?: string;
    current_status?: string;
  };
  created_at: string;
}

export interface NotificationPreference {
  id: string;
  user_id: string;
  shipment_id?: string;
  notification_type: 'milestone' | 'exception' | 'status_change' | 'delay' | 'delivery';
  channels: Array<'in_app' | 'push' | 'email' | 'sms'>;
  is_enabled: boolean;
  quiet_hours_start?: string;
  quiet_hours_end?: string;
  minimum_priority: 'low' | 'medium' | 'high' | 'urgent';
  created_at: string;
  updated_at: string;
}

class NotificationService {
  private realtimeChannel: RealtimeChannel | null = null;

  async getUserNotifications(userId: string, includeRead: boolean = false): Promise<Notification[]> {
    try {
      let query = supabase
        .from('notifications')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (!includeRead) {
        query = query.eq('is_read', false);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data || [];
    } catch (error: any) {
      console.error('Error fetching notifications:', error);
      throw error;
    }
  }

  async getShipmentNotifications(shipmentId: string): Promise<Notification[]> {
    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('shipment_id', shipmentId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error: any) {
      console.error('Error fetching shipment notifications:', error);
      throw error;
    }
  }

  async markAsRead(notificationId: string): Promise<void> {
    try {
      const { error } = await supabase.rpc('mark_notification_read', {
        notification_id: notificationId,
      });

      if (error) throw error;
    } catch (error: any) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  }

  async markAllAsRead(): Promise<void> {
    try {
      const { error } = await supabase.rpc('mark_all_notifications_read');

      if (error) throw error;
    } catch (error: any) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  }

  async getUnreadCount(userId: string): Promise<number> {
    try {
      const { count, error } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('is_read', false);

      if (error) throw error;
      return count || 0;
    } catch (error: any) {
      console.error('Error fetching unread count:', error);
      throw error;
    }
  }

  async getUserPreferences(userId: string): Promise<NotificationPreference[]> {
    try {
      const { data, error } = await supabase
        .from('notification_preferences')
        .select('*')
        .eq('user_id', userId)
        .is('shipment_id', null)
        .order('notification_type');

      if (error) throw error;
      return data || [];
    } catch (error: any) {
      console.error('Error fetching user preferences:', error);
      throw error;
    }
  }

  async getShipmentPreferences(userId: string, shipmentId: string): Promise<NotificationPreference[]> {
    try {
      const { data, error } = await supabase
        .from('notification_preferences')
        .select('*')
        .eq('user_id', userId)
        .eq('shipment_id', shipmentId)
        .order('notification_type');

      if (error) throw error;
      return data || [];
    } catch (error: any) {
      console.error('Error fetching shipment preferences:', error);
      throw error;
    }
  }

  async updatePreference(preferenceId: string, updates: Partial<NotificationPreference>): Promise<NotificationPreference> {
    try {
      const { data, error } = await supabase
        .from('notification_preferences')
        .update(updates)
        .eq('id', preferenceId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error: any) {
      console.error('Error updating preference:', error);
      throw error;
    }
  }

  async createShipmentPreference(
    userId: string,
    shipmentId: string,
    notificationType: NotificationPreference['notification_type'],
    channels: NotificationPreference['channels'],
    isEnabled: boolean = true
  ): Promise<NotificationPreference> {
    try {
      const { data, error } = await supabase
        .from('notification_preferences')
        .insert({
          user_id: userId,
          shipment_id: shipmentId,
          notification_type: notificationType,
          channels,
          is_enabled: isEnabled,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error: any) {
      console.error('Error creating shipment preference:', error);
      throw error;
    }
  }

  subscribeToNotifications(userId: string, callback: (notification: Notification) => void): void {
    if (this.realtimeChannel) {
      this.unsubscribeFromNotifications();
    }

    this.realtimeChannel = supabase
      .channel(`notifications:${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          callback(payload.new as Notification);
        }
      )
      .subscribe();
  }

  unsubscribeFromNotifications(): void {
    if (this.realtimeChannel) {
      supabase.removeChannel(this.realtimeChannel);
      this.realtimeChannel = null;
    }
  }

  async deleteNotification(notificationId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId);

      if (error) throw error;
    } catch (error: any) {
      console.error('Error deleting notification:', error);
      throw error;
    }
  }
}

export const notificationService = new NotificationService();